import { isExpectedSupabaseLockStolenMessage } from "./error-noise";

type PostHogCaptureEvent = {
  event?: string;
  properties?: Record<string, unknown>;
};

const SENSITIVE_URL_PARAM_KEYS = [
  "access_token",
  "dclid",
  "fbclid",
  "gbraid",
  "gclid",
  "msclkid",
  "refresh_token",
  "provider_token",
  "provider_refresh_token",
  "wbraid",
  "expires_at",
  "expires_in",
  "token_type",
  "sb",
];

const URL_PROPERTY_KEYS = new Set([
  "$current_url",
  "$referrer",
  "$initial_current_url",
  "$initial_referrer",
  "current_url",
  "url",
  "href",
  "landing_page",
  "initial_landing_page",
]);

function shouldSanitizeStringProperty(key: string, value: string) {
  return URL_PROPERTY_KEYS.has(key) || value.startsWith("http://") || value.startsWith("https://");
}

export function sanitizePostHogUrlString(value: string): string {
  if (!value) return value;

  try {
    const parsed = new URL(value);
    parsed.hash = "";
    for (const key of SENSITIVE_URL_PARAM_KEYS) {
      parsed.searchParams.delete(key);
    }
    return parsed.toString();
  } catch {
    const hashIndex = value.indexOf("#");
    if (hashIndex === -1) return value;
    return value.slice(0, hashIndex);
  }
}

function sanitizePostHogProperties(properties: Record<string, unknown> | undefined) {
  if (!properties) return;

  for (const [key, value] of Object.entries(properties)) {
    if (typeof value === "string" && shouldSanitizeStringProperty(key, value)) {
      properties[key] = sanitizePostHogUrlString(value);
      continue;
    }

    if (
      (key === "$set" || key === "$set_once") &&
      value &&
      typeof value === "object" &&
      !Array.isArray(value)
    ) {
      sanitizePostHogProperties(value as Record<string, unknown>);
    }
  }
}

export function sanitizePostHogEventUrls<T extends PostHogCaptureEvent | null>(event: T): T {
  if (!event?.properties) return event;
  sanitizePostHogProperties(event.properties);
  return event;
}

function asStringArray(value: unknown): string[] {
  if (typeof value === "string") return [value];
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === "string");
}

function getExceptionList(properties: Record<string, unknown>) {
  const exceptionList = properties.$exception_list;
  return Array.isArray(exceptionList)
    ? exceptionList.filter((entry): entry is Record<string, unknown> => {
        return Boolean(entry && typeof entry === "object" && !Array.isArray(entry));
      })
    : [];
}

function getExceptionTypes(
  exceptionList: Record<string, unknown>[],
  properties: Record<string, unknown>
) {
  return [
    ...asStringArray(properties.$exception_types),
    ...asStringArray(properties.$exception_type),
    ...exceptionList.flatMap((entry) => [
      ...asStringArray(entry.type),
      ...asStringArray(entry.$exception_type),
    ]),
  ];
}

function getExceptionValues(
  exceptionList: Record<string, unknown>[],
  properties: Record<string, unknown>
) {
  return [
    ...asStringArray(properties.$exception_values),
    ...asStringArray(properties.$exception_message),
    ...exceptionList.flatMap((entry) => [
      ...asStringArray(entry.value),
      ...asStringArray(entry.$exception_value),
    ]),
  ];
}

function getExceptionFrameSources(exceptionList: Record<string, unknown>[]) {
  return exceptionList.flatMap((entry) => {
    const stacktrace = entry.stacktrace;
    if (!stacktrace || typeof stacktrace !== "object" || Array.isArray(stacktrace)) {
      return [];
    }

    const frames = (stacktrace as Record<string, unknown>).frames;
    if (!Array.isArray(frames)) return [];

    return frames.flatMap((frame) => {
      if (!frame || typeof frame !== "object" || Array.isArray(frame)) {
        return [];
      }

      return asStringArray((frame as Record<string, unknown>).source);
    });
  });
}

function hasType(types: string[], expectedType: string) {
  return types.some((type) => type === expectedType);
}

function hasValue(values: string[], predicate: (value: string) => boolean) {
  return values.some(predicate);
}

function hasOnlyLiveKitSdkFrames(frameSources: string[]) {
  return (
    frameSources.length > 0 &&
    frameSources.every((source) => source.includes("node_modules/livekit-client"))
  );
}

function hasOnlyPostHogRequestFrames(frameSources: string[]) {
  return (
    frameSources.length > 0 &&
    frameSources.every((source) => {
      return source.includes("node_modules/posthog-js") || source.includes("node_modules/fflate");
    })
  );
}

function hasOnlyAvatarKitFrames(frameSources: string[]) {
  return (
    frameSources.length > 0 &&
    frameSources.every((source) => source.includes("node_modules/@spatius/avatarkit"))
  );
}

function hasOnlyClarityFrames(frameSources: string[]) {
  return (
    frameSources.length > 0 &&
    frameSources.every((source) => source.includes("scripts.clarity.ms/"))
  );
}

function hasOnlyReactDomFrames(frameSources: string[]) {
  return (
    frameSources.length > 0 &&
    frameSources.every((source) => {
      return (
        source.includes("node_modules/react-dom") || source.includes("next/dist/compiled/react-dom")
      );
    })
  );
}

function hasNoResolvedFrames(frameSources: string[]) {
  return frameSources.length === 0;
}

export function shouldSuppressNoisyDomEventException(event: PostHogCaptureEvent | null): boolean {
  if (!event || event.event !== "$exception") {
    return false;
  }

  const properties = event.properties || {};
  const exceptionList = getExceptionList(properties);
  const exceptionTypes = getExceptionTypes(exceptionList, properties);
  const exceptionValues = getExceptionValues(exceptionList, properties);

  const hasDomEventType = hasType(exceptionTypes, "Event");
  const hasDomEventValue = hasValue(exceptionValues, (value) => {
    return (
      value === "[object Event]" ||
      value.includes("Event captured as exception with keys: isTrusted") ||
      value.includes("DOMError: Captured event mismatch")
    );
  });

  return hasDomEventType || hasDomEventValue;
}

export function shouldSuppressExpectedSdkNoise(event: PostHogCaptureEvent | null): boolean {
  if (!event || event.event !== "$exception") {
    return false;
  }

  const properties = event.properties || {};
  const exceptionList = getExceptionList(properties);
  const exceptionTypes = getExceptionTypes(exceptionList, properties);
  const exceptionValues = getExceptionValues(exceptionList, properties);
  const frameSources = getExceptionFrameSources(exceptionList);

  if (
    hasType(exceptionTypes, "SignalRequestError") &&
    hasValue(exceptionValues, (value) =>
      value.includes("Request to update local metadata timed out")
    ) &&
    hasOnlyLiveKitSdkFrames(frameSources)
  ) {
    return true;
  }

  if (
    hasType(exceptionTypes, "UnexpectedConnectionState") &&
    hasValue(exceptionValues, (value) => value.includes("PC manager is closed")) &&
    hasOnlyLiveKitSdkFrames(frameSources)
  ) {
    return true;
  }

  if (
    hasType(exceptionTypes, "DataStreamError") &&
    hasValue(exceptionValues, (value) => {
      return (
        value.includes("Participant agent-") &&
        value.includes("unexpectedly disconnected in the middle of sending data")
      );
    }) &&
    hasOnlyLiveKitSdkFrames(frameSources)
  ) {
    return true;
  }

  if (
    hasType(exceptionTypes, "ConnectionError") &&
    hasValue(exceptionValues, (value) => value === "Client initiated disconnect") &&
    hasOnlyLiveKitSdkFrames(frameSources)
  ) {
    return true;
  }

  if (
    (hasType(exceptionTypes, "NavigatorLockAcquireTimeoutError") ||
      hasType(exceptionTypes, "Error")) &&
    hasValue(exceptionValues, isExpectedSupabaseLockStolenMessage)
  ) {
    return true;
  }

  if (
    hasType(exceptionTypes, "DOMException") &&
    hasValue(exceptionValues, (value) =>
      value.includes("GPUDevice.createCommandEncoder: Unable to make command encoder")
    ) &&
    hasOnlyAvatarKitFrames(frameSources)
  ) {
    return true;
  }

  if (
    hasType(exceptionTypes, "DOMException") &&
    hasValue(exceptionValues, (value) => {
      return (
        value.includes("Failed to execute 'addIceCandidate' on 'RTCPeerConnection'") &&
        value.includes("signalingState is 'closed'")
      );
    })
  ) {
    return true;
  }

  if (
    hasType(exceptionTypes, "NegotiationError") &&
    hasValue(exceptionValues, (value) => {
      return (
        value.includes("Failed to execute 'setRemoteDescription' on 'RTCPeerConnection'") &&
        value.includes("signalingState is 'closed'")
      );
    })
  ) {
    return true;
  }

  if (
    hasType(exceptionTypes, "DOMException") &&
    hasValue(exceptionValues, (value) => {
      return (
        value.includes("Failed to execute 'insertBefore' on 'Node'") &&
        value.includes("is not a child of this node")
      );
    }) &&
    hasOnlyReactDomFrames(frameSources)
  ) {
    return true;
  }

  if (
    hasType(exceptionTypes, "DOMException") &&
    hasValue(exceptionValues, (value) => value === "AbortError: The operation was aborted.") &&
    hasNoResolvedFrames(frameSources)
  ) {
    return true;
  }

  if (
    hasType(exceptionTypes, "DOMException") &&
    hasValue(
      exceptionValues,
      (value) => value === "AbortError: Lock was stolen by another request"
    ) &&
    hasNoResolvedFrames(frameSources)
  ) {
    return true;
  }

  if (
    hasType(exceptionTypes, "DOMException") &&
    hasValue(exceptionValues, (value) => {
      return (
        value.includes("NotAllowedError: The play method is not allowed") &&
        value.includes("user agent or the platform")
      );
    }) &&
    hasNoResolvedFrames(frameSources)
  ) {
    return true;
  }

  if (
    hasType(exceptionTypes, "Error") &&
    hasValue(exceptionValues, (value) => value === "Script error.") &&
    hasNoResolvedFrames(frameSources)
  ) {
    return true;
  }

  if (
    hasType(exceptionTypes, "ChunkLoadError") &&
    hasValue(exceptionValues, (value) => {
      return (
        value.includes("Loading chunk identify failed") && value.includes("/static/identify.js")
      );
    })
  ) {
    return true;
  }

  if (
    hasType(exceptionTypes, "TypeError") &&
    hasValue(exceptionValues, (value) => value.includes("null is not an object")) &&
    hasOnlyPostHogRequestFrames(frameSources)
  ) {
    return true;
  }

  if (hasOnlyClarityFrames(frameSources)) {
    return true;
  }

  return false;
}

export function filterPostHogExceptionEvent<T extends PostHogCaptureEvent | null>(
  event: T
): T | null {
  const sanitizedEvent = sanitizePostHogEventUrls(event);
  return shouldSuppressNoisyDomEventException(sanitizedEvent) ||
    shouldSuppressExpectedSdkNoise(sanitizedEvent)
    ? null
    : sanitizedEvent;
}
